import api, { unwrap } from './client';
import type { ApiResponse } from '@/types/api';
import type {
  WhatsAppAgentAnalytics,
  WhatsAppAgentAvailability,
  WhatsAppAgentAvailabilityUpdateRequest,
  WhatsAppInboxMember,
  WhatsAppInboxMemberRequest,
  WhatsAppInboxQueue,
  WhatsAppInboxQueueDashboard,
  WhatsAppInboxQueueRequest,
  WhatsAppInboxRoutingPolicy,
  WhatsAppInboxRoutingPolicyRequest,
} from '@/types/whatsappTeamInbox';

const BASE = '/v1/social/whatsapp/inbox';

export const whatsappTeamInboxApi = {
  listQueues: () => unwrap(api.get<ApiResponse<WhatsAppInboxQueue[]>>(`${BASE}/queues`)),

  createQueue: (body: WhatsAppInboxQueueRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppInboxQueue>>(`${BASE}/queues`, body)),

  getQueue: (id: number) => unwrap(api.get<ApiResponse<WhatsAppInboxQueue>>(`${BASE}/queues/${id}`)),

  updateQueue: (id: number, body: WhatsAppInboxQueueRequest) =>
    unwrap(api.put<ApiResponse<WhatsAppInboxQueue>>(`${BASE}/queues/${id}`, body)),

  deleteQueue: (id: number) => unwrap(api.delete<ApiResponse<void>>(`${BASE}/queues/${id}`)),

  listMembers: (queueId: number) =>
    unwrap(api.get<ApiResponse<WhatsAppInboxMember[]>>(`${BASE}/queues/${queueId}/members`)),

  upsertMember: (queueId: number, body: WhatsAppInboxMemberRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppInboxMember>>(`${BASE}/queues/${queueId}/members`, body)),

  updateMember: (queueId: number, memberId: number, body: WhatsAppInboxMemberRequest) =>
    unwrap(
      api.put<ApiResponse<WhatsAppInboxMember>>(`${BASE}/queues/${queueId}/members/${memberId}`, body),
    ),

  deleteMember: (queueId: number, memberId: number) =>
    unwrap(api.delete<ApiResponse<void>>(`${BASE}/queues/${queueId}/members/${memberId}`)),

  queueDashboard: (queueId: number) =>
    unwrap(api.get<ApiResponse<WhatsAppInboxQueueDashboard>>(`${BASE}/queues/${queueId}/dashboard`)),

  listRoutingPolicies: () =>
    unwrap(api.get<ApiResponse<WhatsAppInboxRoutingPolicy[]>>(`${BASE}/routing-policies`)),

  createRoutingPolicy: (body: WhatsAppInboxRoutingPolicyRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppInboxRoutingPolicy>>(`${BASE}/routing-policies`, body)),

  getRoutingPolicy: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppInboxRoutingPolicy>>(`${BASE}/routing-policies/${id}`)),

  updateRoutingPolicy: (id: number, body: WhatsAppInboxRoutingPolicyRequest) =>
    unwrap(api.put<ApiResponse<WhatsAppInboxRoutingPolicy>>(`${BASE}/routing-policies/${id}`, body)),

  activateRoutingPolicy: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppInboxRoutingPolicy>>(`${BASE}/routing-policies/${id}/activate`)),

  deleteRoutingPolicy: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`${BASE}/routing-policies/${id}`)),

  getAvailability: (employeeId: number) =>
    unwrap(
      api.get<ApiResponse<WhatsAppAgentAvailability>>(`${BASE}/agents/${employeeId}/availability`),
    ),

  updateAvailability: (employeeId: number, body: WhatsAppAgentAvailabilityUpdateRequest) =>
    unwrap(
      api.put<ApiResponse<WhatsAppAgentAvailability>>(
        `${BASE}/agents/${employeeId}/availability`,
        body,
      ),
    ),

  heartbeat: (employeeId: number) =>
    unwrap(api.post<ApiResponse<WhatsAppAgentAvailability>>(`${BASE}/agents/${employeeId}/heartbeat`)),

  agentAnalytics: (employeeId: number, params?: { from?: string; to?: string }) =>
    unwrap(
      api.get<ApiResponse<WhatsAppAgentAnalytics>>(`${BASE}/agents/${employeeId}/analytics`, {
        params,
      }),
    ),

  teamAnalytics: (teamId: number, params?: { from?: string; to?: string }) =>
    unwrap(
      api.get<ApiResponse<WhatsAppAgentAnalytics>>(`${BASE}/teams/${teamId}/analytics`, { params }),
    ),
};
